import { FC } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ProgressIndicator, StepData } from '@mrshmllw/smores-react'

import { postQuoteOptions, removeStepsOptions } from './config'
import { getAdditionalQuestionsSessionStorage } from 'postQuote/pages/AdditionalQuestions/utils/sessionStorage'
import { shouldSeeOwnershipAdditionalQuestions } from 'postQuote/utils/shouldSeeOwnershipAdditionalQuestions/shouldSeeOwnershipAdditionalQuestions'
import { shouldShowDirectFeature } from 'postQuote/utils/showDirectPcw.helper'

import { useGetQuoteV2Query } from 'app/services'
import { usePaymentPlan } from 'src/router/post-quote/providers/PaymentPlanProvider'
import { useEventTracking, useFeatureFlags } from 'utils'
import { resolveProgressBarSteps } from 'utils/progressIndicatorHelpers'

export const PostQuoteProgressBar: FC = () => {
  const navigate = useNavigate()
  const { quoteId } = useParams()

  if (!quoteId) {
    throw new Error('Progress bar used on a route without a quote ID')
  }

  const track = useEventTracking()
  const { paymentPlan } = usePaymentPlan()
  const { data: quoteData, error: quoteErr } = useGetQuoteV2Query({ quoteId })
  const { postQuoteFeatureFlags } = useFeatureFlags()
  const { skip_addons_direct } = postQuoteFeatureFlags

  const channel = quoteData?.channel.name

  const isRemoveStepsDirect = shouldShowDirectFeature(!!quoteData?.removeStepsOnDirect, channel)
  const isRemoveSteps = isRemoveStepsDirect
  const requiredAdditionalQuestions = quoteData?.requiredAdditionalQuestions
  const previousCancellationSessionStorage = getAdditionalQuestionsSessionStorage({
    quoteId,
  })?.previousCancellation
  const licenceSessionStorage = getAdditionalQuestionsSessionStorage({ quoteId })?.licenceQuestions
  const studentSessionStorage = getAdditionalQuestionsSessionStorage({ quoteId })?.studentQuestions

  const progressBar = resolveProgressBarSteps({
    stepMapping: isRemoveSteps ? removeStepsOptions : postQuoteOptions,
    options: { ignoredPaths: ['/success', '/decline', '/error', '/timeout', '/continue-quote'] },
  })

  if (!progressBar) {
    return null
  }

  const routeToNewPath = ({ id }: StepData) => {
    switch (id) {
      case 'overview': {
        if (
          requiredAdditionalQuestions &&
          shouldSeeOwnershipAdditionalQuestions(requiredAdditionalQuestions)
        ) {
          navigate(
            `/quote/${quoteId}/additional-questions/ownership?payment=${paymentPlan}&productPlan=${quoteData?.productPlan}`,
          )
          break
        }
        navigate(`/quote/${quoteId}/overview?payment=${paymentPlan}`)
        break
      }
      case 'upgrade': {
        navigate(
          `/quote/${quoteId}/plan-comparison?productPlan=${quoteData?.productPlan}&payment=${paymentPlan}`,
        )
        break
      }
      case 'add-on': {
        if (channel && shouldShowDirectFeature(skip_addons_direct, channel)) {
          navigate(`/quote/${quoteId}/${id}/breakdown?payment=${paymentPlan}`)
        } else {
          navigate(`/quote/${quoteId}/${id}/general?payment=${paymentPlan}`)
        }
        break
      }
      case 'account': {
        if (!quoteData) return

        if (
          (requiredAdditionalQuestions &&
            requiredAdditionalQuestions.includes('policy-holder-previous-cancellation')) ||
          previousCancellationSessionStorage
        ) {
          navigate(
            `/quote/${quoteId}/additional-questions/previous-cancellation?payment=${paymentPlan}`,
          )
        } else if (
          (requiredAdditionalQuestions &&
            requiredAdditionalQuestions.includes('policy-holder-licence-country')) ||
          licenceSessionStorage
        ) {
          navigate(`/quote/${quoteId}/additional-questions/licence-country?payment=${paymentPlan}`)
        } else if (
          (requiredAdditionalQuestions &&
            (requiredAdditionalQuestions.includes('policy-holder-student') ||
              requiredAdditionalQuestions.includes('policy-holder-secondary-employment'))) ||
          studentSessionStorage
        ) {
          navigate(`/quote/${quoteId}/additional-questions/student?payment=${paymentPlan}`)
        }

        navigate(`/quote/${quoteId}/create-account?payment=${paymentPlan}`)
        break
      }
      case 'payment': {
        navigate(`/quote/${quoteId}/payment/${paymentPlan}`)
        break
      }
      default: {
        navigate(`/quote/${quoteId}/${id}?payment=${paymentPlan}`)
      }
    }
  }

  if (quoteErr) {
    track('Post quote progress bar rendered as Error page')
    return null
  }

  return (
    <ProgressIndicator
      steps={isRemoveSteps ? removeStepsOptions : postQuoteOptions}
      currentStep={progressBar.currentStep}
      handleStepChange={routeToNewPath}
      onStepClick={({ currentStepIndex, currentStepState }) => {
        track('progressBar', {
          type: 'Post quote',
          activeButton: currentStepState !== 'disabled',
          clicked: postQuoteOptions[currentStepIndex].label,
          from: progressBar.currentPath,
        })
      }}
    />
  )
}
